import { inject, Injectable } from '@angular/core';
import { TapestryNotificationService } from '@predictiveindex/tapestry';
import { BehaviorSubject, catchError, map, Observable, of, tap } from 'rxjs';
import { LocalStorageService, SessionStorageService } from '../../cache';
import {
    AccountProvisionReferringSource,
    CompanyClassificationType
} from '../../constants-types';
import {
    ClassificationType,
    DemoAccountSignalRNotificationData,
    DemoAccountSignalrNotificationStatus,
    ProspectData,
    SellerToolsAccountTableData
} from '../../core-data-model';
import {
    InitDataService,
    UserProfileSettingsService
} from '../../system-services';
import { TranslationService } from '../../translations';
import { CompaniesDataService } from '../Companies.dataservice';
import { HashService } from '../HashService';
import { DemoAccountsRepositoryService } from './demo-account-repository.service';

/**
 * Service for managing demo accounts
 */
@Injectable({
    providedIn: 'root'
})
export class DemoAccountService {
    private TR = inject(TranslationService);
    private userProfileSettings = inject(UserProfileSettingsService);
    private demoAccountsRepository = inject(DemoAccountsRepositoryService);
    private notification = inject(TapestryNotificationService);
    private userProfile = inject(UserProfileSettingsService);
    private sessionStorage = inject(SessionStorageService);
    private initData = inject(InitDataService);
    private hashService = inject(HashService);
    private localStorage = inject(LocalStorageService);
    private companiesData = inject(CompaniesDataService);

    private notificationQueue: Record<string, string> = {};
    private _demoAccounts = new BehaviorSubject<
        SellerToolsAccountTableData[] | undefined
    >(undefined);
    private _workshopAccounts = new BehaviorSubject<
        SellerToolsAccountTableData[] | undefined
    >(undefined);
    private _createProspectDemo = new BehaviorSubject<boolean>(true);

    /**
     * Observable for demo accounts
     */
    demoAccounts$ = this._demoAccounts.asObservable();

    /**
     * Observable for workshop accounts
     */
    workshopAccounts$ = this._workshopAccounts.asObservable();

    /**
     * Observable for creating prospect demo
     */
    createProspectDemo$ = this._createProspectDemo.asObservable();

    /**
     * Create a new demo account
     *
     * @param prospectName
     * @param prospectData
     * @param classificationType
     * @param referringSource
     */
    create(
        prospectName: string,
        prospectData: ProspectData[],
        classificationType: ClassificationType = ClassificationType.DynamicDemo,
        referringSource: AccountProvisionReferringSource = AccountProvisionReferringSource.PISoftware
    ) {
        const formattedProspectName = prospectName.trim();
        return this.demoAccountsRepository
            .createCompany({
                companyName: formattedProspectName,
                requestingUserId: this.userProfileSettings.myUserId,
                requestingCompanyGuid: this.userProfileSettings.currentCompany,
                thirdPartyAccessIds: [this.userProfileSettings.myUserId],
                classificationType,
                prospectData,
                referringSource
            })
            .pipe(
                catchError(() => {
                    this.notification.create({
                        message: this.TR.key('text.error.body'),
                        type: 'error',
                        duration: 3000
                    });
                    return of(null);
                }),
                tap(async (response) => {
                    if (response && response.statusCode < 400) {
                        await this.processNotificationQueue({
                            CompanyName: response.companyName,
                            CompanyGuid: null,
                            Status: 'CreatingCompany',
                            CompanyClassificationType:
                                response.companyClassificationType
                        });
                    }
                })
            );
    }

    /**
     * Get workshop accounts
     */
    getWorkshopAccounts(): Observable<SellerToolsAccountTableData[]> {
        return this.demoAccountsRepository.getDemoAccounts().pipe(
            catchError(() => {
                this.notification.create({
                    message: this.TR.key('text.error.body'),
                    type: 'error',
                    duration: 3000
                });
                return of(null);
            }),
            map((response) =>
                response
                    .filter(
                        (account) =>
                            this.companiesData.companies.find(
                                (company) =>
                                    company.id === account.demoCompanyGuid
                            ) &&
                            account.demoDataType ===
                                ClassificationType.Workshop &&
                            account.referringSource ===
                                AccountProvisionReferringSource.PISoftware &&
                            !!account.name
                    )
                    .map((account) => ({
                        accountName: account.name,
                        lastActive: this.getLastActiveTime(
                            account.modifiedTime
                        ),
                        companyGuid: account.demoCompanyGuid,
                        demoDataClassificationType: account.demoDataType
                    }))
            )
        );
    }

    /**
     * Get prospect demo accounts
     */
    getProspectDemoAccounts(): Observable<SellerToolsAccountTableData[]> {
        return this.demoAccountsRepository.getDemoAccounts().pipe(
            catchError(() => {
                this.notification.create({
                    message: this.TR.key('text.error.body'),
                    type: 'error',
                    duration: 3000
                });
                return of(null);
            }),
            map((response) =>
                response
                    .filter(
                        (account) =>
                            this.companiesData.companies.find(
                                (company) =>
                                    company.id === account.demoCompanyGuid
                            ) &&
                            account.demoDataType ===
                                ClassificationType.DynamicDemo &&
                            account.referringSource ===
                                AccountProvisionReferringSource.PISoftware &&
                            !!account.name
                    )
                    .map((account) => ({
                        accountName: account.name,
                        lastActive: this.getLastActiveTime(
                            account.modifiedTime
                        ),
                        companyGuid: account.demoCompanyGuid,
                        demoDataClassificationType: account.demoDataType
                    }))
            )
        );
    }

    /**
     * Set demo accounts
     *
     * @param demoAccounts
     */
    setDemoAccounts(demoAccounts: SellerToolsAccountTableData[]) {
        this._demoAccounts.next(demoAccounts);
    }

    /**
     * Set workshop accounts
     *
     * @param workshopAccounts
     */
    setWorkshopAccounts(workshopAccounts: SellerToolsAccountTableData[]) {
        this._workshopAccounts.next(workshopAccounts);
    }

    /**
     * On create prospect demo event
     */
    onCreateProspectDemo() {
        this._createProspectDemo.next(true);
    }

    /**
     * Process notification queue
     *
     * @param message
     */
    async processNotificationQueue(
        message: DemoAccountSignalRNotificationData
    ) {
        const messageRecord = this.getMessageRecord(message);

        if (!(message.CompanyName in this.notificationQueue)) {
            this.notificationQueue[message.CompanyName] = null;
        }

        if (this.notificationQueue[message.CompanyName]) {
            this.notification.remove(
                this.notificationQueue[message.CompanyName]
            );
        }

        if (message.Status === 'Completed') {
            // Add company to local storage to bypass the cache
            await this.companiesData.addCompany(
                message.CompanyGuid,
                message.CompanyName,
                message.CompanyClassificationType
            );

            if (
                message.CompanyClassificationType ===
                CompanyClassificationType.DynamicDemo
            ) {
                const demoAccounts = this._demoAccounts.getValue();
                this._demoAccounts.next(
                    demoAccounts.concat({
                        accountName: message.CompanyName,
                        lastActive: this.getLastActiveTime(
                            new Date().toISOString()
                        ),
                        companyGuid: message.CompanyGuid,
                        demoDataClassificationType:
                            ClassificationType.DynamicDemo
                    })
                );
            } else {
                const workshopAccounts = this._workshopAccounts.getValue();
                this._workshopAccounts.next(
                    workshopAccounts.concat({
                        accountName: message.CompanyName,
                        lastActive: this.getLastActiveTime(
                            new Date().toISOString()
                        ),
                        companyGuid: message.CompanyGuid,
                        demoDataClassificationType: ClassificationType.Workshop
                    })
                );
            }

            this.notification.create(
                {
                    message: messageRecord[message.Status],
                    type: 'success',
                    action: async () => {
                        await this.changeCompany(message.CompanyGuid);
                    },
                    actionLabel: this.TR.key(
                        'platform.oi.demo-accounts.websocket-notification.go-to-demo-account'
                    )
                },
                true
            );
            return;
        }

        if (
            message.Status === 'AddedUsers' ||
            message.Status === 'AddedThirdPartyUsers'
        ) {
            this.notification.create({
                message: messageRecord[message.Status],
                type: 'success',
                action: async () => {
                    await this.changeCompany(message.CompanyGuid);
                },
                actionLabel: this.TR.key(
                    'platform.oi.demo-accounts.websocket-notification.go-to-demo-account'
                )
            });
            return;
        }

        if (message.Status === 'Failed') {
            this.notification.create(
                {
                    message: messageRecord[message.Status],
                    type: 'error'
                },
                true
            );
            return;
        }

        this.notificationQueue[message.CompanyName] = this.notification.create(
            {
                message: messageRecord[message.Status],
                type: 'processing'
            },
            true
        );
    }

    /**
     * Comparator for sorting last active column
     *
     * @param valueA
     * @param valueB
     */
    lastActiveComparator(valueA: string, valueB: string) {
        const numA = parseInt(valueA.split(' ')[0], 10);
        const numB = parseInt(valueB.split(' ')[0], 10);

        if (valueA === this.TR.key('common.today')) return -1;
        if (valueB === this.TR.key('common.today')) return 1;
        if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB;
        } else if (!isNaN(numA)) {
            return -1;
        } else if (!isNaN(numB)) {
            return 1;
        } else {
            return valueA.localeCompare(valueB);
        }
    }

    private async changeCompany(companyId: string) {
        if (companyId !== this.userProfile.currentCompany) {
            await this.userProfile.setCurrentCompany(companyId);
            this.sessionStorage.clearAll();
            await this.storeSelectedCompanyId(companyId);
            window.location.href = '/';
        }
    }

    private async storeSelectedCompanyId(companyId: string) {
        const currentUserEmail = this.initData.user.email.toLowerCase();
        const hashedEmail =
            await this.hashService.computeHashHex(currentUserEmail);
        this.localStorage.setItem(`${hashedEmail}-currentCompany`, companyId);
    }

    private getMessageRecord(
        message: DemoAccountSignalRNotificationData
    ): Record<DemoAccountSignalrNotificationStatus, string> {
        return {
            Unknown: null,
            CreatingCompany: this.TR.keyFormatter(
                'platform.oi.demo-accounts.websocket-notification.create-company',
                message.CompanyName
            ),
            AddingUsers: this.TR.keyFormatter(
                'platform.oi.demo-accounts.websocket-notification.updating-users',
                message.CompanyName
            ),
            AddingThirdPartyUsers: this.TR.keyFormatter(
                'client-core.seller-experience.third-party-users.updating-third-party-users',
                message.CompanyName
            ),
            CreatingUsers: this.TR.keyFormatter(
                'platform.oi.demo-accounts.websocket-notification.create-users',
                message.CompanyName
            ),
            CreatingTeams: this.TR.keyFormatter(
                'platform.oi.demo-accounts.websocket-notification.create-teams',
                message.CompanyName
            ),
            CreatingJobs: this.TR.keyFormatter(
                'platform.oi.demo-accounts.websocket-notification.create-jobs',
                message.CompanyName
            ),
            CreatingSurveyUsers: this.TR.keyFormatter(
                'platform.oi.demo-accounts.websocket-notification.create-survey-users"',
                message.CompanyName
            ),
            Failed: this.TR.keyFormatter(
                'platform.oi.demo-accounts.websocket-notification.failed',
                message.CompanyName
            ),
            AddedUsers: this.TR.keyFormatter(
                'platform.oi.demo-accounts.websocket-notification.updated-users',
                message.CompanyName
            ),
            AddedThirdPartyUsers: this.TR.keyFormatter(
                'client-core.seller-experience.third-party-users.updated-third-party-users',
                message.CompanyName
            ),
            Completed: this.TR.keyFormatter(
                'platform.oi.demo-accounts.websocket-notification.success',
                message.CompanyName
            ),
            UpdateFailed: this.TR.keyFormatter(
                'platform.oi.demo-accounts.websocket-notification.updating-failed',
                message.CompanyName
            )
        };
    }

    private getLastActiveTime(modifiedTime: string) {
        const oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
        const lastActiveDate = new Date(modifiedTime);
        const now = Date.now();

        const diffDays = Math.round(
            Math.abs((now - lastActiveDate.getTime()) / oneDay)
        );
        if (diffDays === 0) {
            return this.TR.key('common.today');
        } else {
            return `${diffDays} ${this.TR.key(
                'seller-tools-panel.last-active-suffix'
            )}`;
        }
    }
}
